import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

import { useLanguage } from "@/contexts/LanguageContext";

import teamLucy from "@/assets/team-lucy-optimized.webp";
import teamAlex from "@/assets/team-alex.webp";
import teamYsabel from "@/assets/team-ysabel.webp";
import teamDenise from "@/assets/team-denise.webp";
import teamGraziela from "@/assets/GS.webp";
import teamJennifer from "@/assets/team-jennifer.webp";

interface TeamMember {
  id: number;
  name: string;
  firstName: string;
  lastName: string;
  roleKey: string;
  bioKey: string;
  image: string;
  bgLetter: string;
}

const teamMembers: TeamMember[] = [
  {
    id: 1,
    name: "LUCY ARANDIA",
    firstName: "LUCY",
    lastName: "ARANDIA",
    roleKey: "team.member.lucy.role",
    bioKey: "team.member.lucy.bio",
    image: teamLucy,
    bgLetter: "L",
  },
  {
    id: 2,
    name: "ALEXANDER ARANDIA",
    firstName: "ALEXANDER",
    lastName: "ARANDIA",
    roleKey: "team.member.alexander.role",
    bioKey: "team.member.alexander.bio",
    image: teamAlex,
    bgLetter: "A",
  },
  {
    id: 3,
    name: "YSABEL RODRIGUEZ",
    firstName: "YSABEL",
    lastName: "RODRIGUEZ",
    roleKey: "team.member.ysabel.role",
    bioKey: "team.member.ysabel.bio",
    image: teamYsabel,
    bgLetter: "Y",
  },
  {
    id: 4,
    name: "DENISE TORRES",
    firstName: "DENISE",
    lastName: "TORRES",
    roleKey: "team.member.denise.role",
    bioKey: "team.member.denise.bio",
    image: teamDenise,
    bgLetter: "D",
  },
  {
    id: 5,
    name: "GRAZIELA PONTES",
    firstName: "GRAZIELA",
    lastName: "PONTES",
    roleKey: "team.member.graziela.role",
    bioKey: "team.member.graziela.bio",
    image: teamGraziela,
    bgLetter: "G",
  },
  {
    id: 6,
    name: "JENNIFFER PAULINO",
    firstName: "JENNIFFER",
    lastName: "PAULINO",
    roleKey: "team.member.jenniffer.role",
    bioKey: "team.member.jenniffer.bio",
    image: teamJennifer,
    bgLetter: "J",
  },
];

const useImagePreloader = (images: string[]) => {
  useEffect(() => {
    images.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, [images]);
};

const EditorialDepthSlider = () => {
  const { t } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const imageUrls = teamMembers.map((m) => m.image);
  useImagePreloader(imageUrls);

  const handleSlideChange = useCallback(
    async (newDirection: number) => {
      if (isAnimating) return;
      const nextIndex = (currentIndex + newDirection + teamMembers.length) % teamMembers.length;
      const nextMember = teamMembers[nextIndex];
      setIsAnimating(true);
      try {
        const img = new Image();
        img.src = nextMember.image;
        await img.decode();
      } catch {}
      setDirection(newDirection);
      setCurrentIndex(nextIndex);
      setTimeout(() => setIsAnimating(false), 600);
    },
    [currentIndex, isAnimating],
  );

  const member = teamMembers[currentIndex];

  return (
    <section id="team" className="relative bg-primary overflow-hidden py-8 lg:py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Eyebrow */}
        <div className="mb-10 lg:mb-12">
          <p className="text-accent text-sm font-medium uppercase tracking-[0.2em] mb-3">{t("team.eyebrow.experts")}</p>
          <h2 className="text-primary-foreground text-4xl md:text-5xl font-heading font-bold leading-tight max-w-md">
            {t("team.title")}
          </h2>
        </div>

        {/* Main Slider Area - Isolating Mobile Grid vs Desktop Block */}
        <div className="relative w-full min-h-[600px] lg:min-h-[550px] grid grid-cols-1 lg:block">
          {/* Gold floor line (Static) */}
          <div className="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-accent/30 z-20" />

          {/* Navigation Buttons (Static & Anchored to bottom left for Desktop) */}
          <div className="hidden lg:flex absolute bottom-0 left-0 z-30 pb-12 items-center gap-3">
            <div className="w-1 flex-shrink-0 bg-transparent rounded-full" />
            <button
              aria-label="Previous team member"
              onClick={() => handleSlideChange(-1)}
              className="w-11 h-11 rounded-full border border-accent/30 flex items-center justify-center text-accent/70 hover:bg-accent hover:text-primary hover:border-accent transition-all duration-300"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              aria-label="Next team member"
              onClick={() => handleSlideChange(1)}
              className="w-11 h-11 rounded-full border border-accent/30 flex items-center justify-center text-accent/70 hover:bg-accent hover:text-primary hover:border-accent transition-all duration-300"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <span className="ml-3 text-accent/40 text-sm font-medium tracking-wide">
              {currentIndex + 1} / {teamMembers.length}
            </span>
          </div>

          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={currentIndex}
              className="col-start-1 row-start-1 w-full relative lg:absolute lg:inset-0 lg:h-full"
              initial="initial"
              animate="animate"
              exit="exit"
            >
              {/* ========== DESKTOP LAYOUT ========== */}
              <div className="hidden lg:grid grid-cols-12 gap-8 h-full relative w-full">
                {/* Left Column: Text vertically centered */}
                <motion.div
                  className="col-span-5 relative z-20 flex flex-col justify-center h-full pb-10"
                  initial={{ opacity: 0, x: -40 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 40 }}
                  transition={{ duration: 0.5, ease: "easeOut", delay: 0.2 }}
                >
                  <div className="flex gap-6">
                    <div className="w-1 flex-shrink-0 bg-accent/40 rounded-full" />
                    <div className="flex flex-col">
                      <p className="text-accent text-[11px] uppercase tracking-[0.2em] mb-3 font-medium">
                        {t(member.roleKey)}
                      </p>
                      <h3 className="text-primary-foreground text-3xl xl:text-4xl font-heading font-bold mb-0 leading-tight">
                        {member.firstName}
                      </h3>
                      <h3 className="text-primary-foreground/60 text-2xl xl:text-3xl font-heading font-bold mb-3 leading-tight">
                        {member.lastName}
                      </h3>
                      <p className="text-primary-foreground/70 text-sm leading-relaxed mb-6 max-w-sm">
                        {t(member.bioKey)}
                      </p>
                      <a
                        href="#contact"
                        className="inline-flex items-center gap-2 text-accent text-sm font-medium hover:gap-3 transition-all duration-300 w-fit"
                      >
                        {t("team.readMore")}
                        <ArrowRight className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </motion.div>

                {/* Right Column: Stage & Image */}
                <div className="col-span-7 relative flex items-end justify-center h-full">
                  <span className="absolute inset-0 flex items-center justify-center pointer-events-none select-none font-heading font-bold text-[25rem] text-white/[0.04] leading-none">
                    {member.bgLetter}
                  </span>

                  <motion.div
                    className="absolute bottom-0 left-0 w-full flex justify-center z-10"
                    initial={{ y: 80, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -40, opacity: 0 }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
                  >
                    <div className="relative flex justify-center w-full">
                      <img
                        src={member.image}
                        alt={member.name}
                        draggable="false"
                        className="h-[550px] w-auto object-contain object-bottom drop-shadow-[0_20px_40px_rgba(0,0,0,0.5)]"
                      />
                      {/* Fixed Desktop Gradient */}
                      <div className="absolute bottom-0 left-0 right-0 h-[35%] bg-gradient-to-t from-primary via-primary/80 to-transparent pointer-events-none z-10" />
                    </div>
                  </motion.div>
                </div>
              </div>

              {/* ========== MOBILE LAYOUT ========== */}
              <div className="flex lg:hidden flex-col items-center h-full pb-4">
                <motion.div
                  className="text-center mb-[-2rem] relative z-0 select-none"
                  initial={{ x: direction >= 0 ? -60 : 60, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: direction >= 0 ? 60 : -60, opacity: 0 }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                  <span className="block text-[15vw] font-heading font-bold leading-[0.85] text-gold-gradient opacity-25">
                    {member.firstName}
                  </span>
                  <span className="block text-[10vw] font-heading font-bold leading-[0.85] text-gold-gradient opacity-15">
                    {member.lastName}
                  </span>
                </motion.div>

                <motion.div
                  className="relative z-10 flex-shrink-0 w-full flex justify-center"
                  initial={{ y: 60, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -30, opacity: 0 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
                >
                  <img
                    src={member.image}
                    alt={member.name}
                    draggable="false"
                    className="h-[300px] w-auto object-contain object-bottom drop-shadow-[0_15px_30px_rgba(0,0,0,0.4)] relative z-10"
                  />
                  <div className="absolute -bottom-2 left-0 right-0 h-[60%] bg-gradient-to-t from-primary via-primary/90 to-transparent pointer-events-none z-20" />
                </motion.div>

                <motion.div
                  className="relative z-30 w-full mt-[-1rem]"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.4, ease: "easeOut", delay: 0.25 }}
                >
                  <div className="bg-white/[0.07] backdrop-blur-md border border-accent/30 rounded-lg p-5">
                    <p className="text-accent text-[11px] uppercase tracking-[0.2em] mb-1 font-medium">
                      {t(member.roleKey)}
                    </p>
                    <h3 className="text-primary-foreground text-lg font-heading font-bold mb-2">{member.name}</h3>
                    <p className="text-primary-foreground/70 text-sm leading-relaxed">{t(member.bioKey)}</p>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Mobile Navigation (Static - Pushes down safely if content grows) */}
        <div className="flex lg:hidden items-center justify-center w-full gap-4 mt-8 pb-6 relative z-30">
          <button
            aria-label="Previous team member"
            onClick={() => handleSlideChange(-1)}
            className="w-11 h-11 rounded-full border border-accent/30 flex items-center justify-center text-accent/70 hover:bg-accent hover:text-primary hover:border-accent transition-all duration-300"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            aria-label="Next team member"
            onClick={() => handleSlideChange(1)}
            className="w-11 h-11 rounded-full border border-accent/30 flex items-center justify-center text-accent/70 hover:bg-accent hover:text-primary hover:border-accent transition-all duration-300"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <span className="ml-auto text-accent/40 text-sm font-medium tracking-wide">
            {currentIndex + 1} / {teamMembers.length}
          </span>
        </div>
      </div>
    </section>
  );
};

export default EditorialDepthSlider;
